import { useState, useEffect, useCallback, useMemo } from "react";
import { Box, Button, TextField } from "@mui/material";
import * as hooks from "lib/hooks";
import * as result from "lib/result";
import { GetTextFieldStyle } from "lib/styles/textField";
import { UseHasPermission } from "lib/hooks/useHasPermission";
import { AddSectorAdminRequest } from "models/dto/user";
import { GMIDField } from "../Forms/GMIDField";
import { NameField } from "../Forms/NameField";
import { ROLE_NAME_SECTOR_ADMIN } from "../../constants";
import type { UserFormData } from "../../types";
import { useForm } from "react-hook-form";
import { SectorDropdown } from "../Forms/SectorDropdown";
import { UserManagementDialogShell } from "../UserManagementDialogShell";
import { AlreadySectorAdminMessage } from "lib/util";

export function AddSectorAdminDialog({
	open,
	onClose,
	onSuccess,
	existingSectorAdminUserIds = [],
}: {
	open: boolean;
	onClose: () => void;
	onSuccess?: () => void;
	/** Business user IDs (GMIN) of users who are already sector admins; used to block adding again */
	existingSectorAdminUserIds?: string[];
}) {
	const api = hooks.UseMccApi();
	const snack = hooks.UseSnackBar();
	const permissions = UseHasPermission();

	const [formData, setFormData] = useState<UserFormData>({
		gmid: "",
		username: "",
		email: "",
		gmin: "",
		name: "",
		phone: "",
		phoneExt: "",
	});

	const [sectorAdminRoleId, setSectorAdminRoleId] = useState<string | null>(
		null
	);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string>("");

	// React Hook Form for sectors field
	type sectorFormData = {
		sectors: { id: string; name: string }[];
	};
	const { control, watch, setValue, reset, trigger } = useForm<sectorFormData>({
		defaultValues: {
			sectors: [],
		},
		mode: "onChange", // Enable validation on change
		reValidateMode: "onChange", // Re-validate on change
	});
	const selectedSectors = watch("sectors");

	// Load Sector Admin role ID when dialog opens
	useEffect(() => {
		if (!open) return;

		const loadRole = async () => {
			try {
				setLoading(true);
				const roles = await api.Roles();
				const sectorAdminRole = roles.find(
					(r) => r.name === ROLE_NAME_SECTOR_ADMIN
				);
				if (sectorAdminRole) {
					setSectorAdminRoleId(sectorAdminRole.id);
				} else {
					setError("Sector Admin role not found");
				}
			} catch (err: any) {
				setError(
					err?.response?.data?.message ||
						err?.message ||
						"Failed to load roles"
				);
			} finally {
				setLoading(false);
			}
		};
		loadRole();
	}, [open, api]);

	// Reset form when dialog opens/closes
	useEffect(() => {
		if (!open) {
			setFormData({
				gmid: "",
				username: "",
				email: "",
				gmin: "",
				name: "",
				phone: "",
				phoneExt: "",
			});
			reset({ sectors: [] });
			setError("");
		}
	}, [open, reset]);

	const handleGMIDCheck = useCallback(async () => {
		if (!formData.gmid.trim()) {
			return { ok: false, msg: "Enter a GMID first" };
		}

		try {
			// Search for user in directory using SearchGmid
			const users = await api.SearchGmid(formData.gmid.trim());
			if (!users.length) {
				return { ok: false, msg: "No user found" };
			}

			const user = users[0];
			const newFormData = {
				...formData,
				username:
					user.onPremisesSamAccountName ||
					formData.gmid.split("@")[0] ||
					"",
				email: user.mail || "",
				gmin: user.employeeId || "",
				name: user.displayName || "",
			};

			setFormData(newFormData);
			return { ok: true, msg: `Found ${user.displayName}` };
		} catch (err: any) {
			return {
				ok: false,
				msg:
					err?.response?.data?.message ||
					err?.message ||
					"Lookup failed",
			};
		}
	}, [formData, api]);

	const isFormValid = useMemo(() => {
		// Check all required fields
		if (
			!formData.gmid ||
			!formData.username ||
			!formData.email ||
			!formData.gmin ||
			!formData.name
		) {
			return false;
		}

		// Check at least one sector selected
		if (
			!selectedSectors ||
			!Array.isArray(selectedSectors) ||
			selectedSectors.length === 0
		) {
			return false;
		}

		// Check role ID is loaded
		if (!sectorAdminRoleId) {
			return false;
		}

		return true;
	}, [formData, selectedSectors, sectorAdminRoleId]);

	const handleSave = useCallback(async () => {
		if (!sectorAdminRoleId) return;

		// Trigger validation before saving - this will mark the field as touched and show errors
		const isValid = await trigger("sectors");
		if (!isValid) {
			// Error will be shown by the SectorDropdown component
			return;
		}

		if (!isFormValid) return;

		const gmin = formData.gmin?.trim();
		if (gmin && existingSectorAdminUserIds.includes(gmin)) {
			setError(AlreadySectorAdminMessage);
			return;
		}

		try {
			setSaving(true);
			setError("");

			const sectorIdsToAdd = selectedSectors
				.map((s) => s?.id)
				.filter((id): id is string => Boolean(id));

			const request: AddSectorAdminRequest = {
				userModel: {
					businessUser: {
						id: formData.gmin,
						name: formData.name,
						contact: {
							name: formData.name,
							email: formData.email,
						},
					},
				},
				sectorIdsToAdd,
				sectorIdsToRemove: [],
			};

			await api.AddSectorAdmin(request);

			snack.open(result.Ok("Sector admin created successfully"));
			onSuccess?.();
			onClose();
		} catch (err: any) {
			const errorMsg =
				err?.response?.data?.errorList?.[0]?.message ||
				err?.response?.data?.message ||
				err?.message ||
				"Failed to create sector admin";
			setError(errorMsg);
			snack.open(result.Err(errorMsg));
		} finally {
			setSaving(false);
		}
	}, [
		isFormValid,
		formData,
		selectedSectors,
		sectorAdminRoleId,
		existingSectorAdminUserIds,
		api,
		snack,
		onClose,
		onSuccess,
	]);

	if (!permissions.isSectorAdmin) return null;

	return (
		<UserManagementDialogShell
			open={open}
			onClose={onClose}
			title="Add Sector Admin"
			maxWidth="xs"
			loading={loading && !sectorAdminRoleId}
			error={error}
			primaryButton={{
				label: "Add",
				onClick: handleSave,
				disabled: !isFormValid || saving,
				loading: saving,
			}}
		>
			<GMIDField
							value={formData.gmid}
							setValue={(v) => {
								setFormData((prev) => ({ ...prev, gmid: v }));
							}}
							disabled={saving}
							editing={false}
							onClearError={() => setError("")}
							onCheckGMID={handleGMIDCheck}
						/>

						{/* Name Field */}
						<NameField
							fullName={formData.name}
							setFullName={(name) =>
								setFormData((prev) => ({ ...prev, name }))
							}
						/>

						{/* GMIN Field - Read-only (populated from API) */}
						<TextField
							label="GMIN"
							value={formData.gmin}
							onChange={(e) =>
								setFormData((prev) => ({
									...prev,
									gmin: e.target.value,
								}))
							}
							size="small"
							sx={GetTextFieldStyle()}
							InputProps={{
								sx: {
									"height": 40,
									"& .MuiInputBase-input": { py: 1 },
									"backgroundColor":
										"action.disabledBackground",
								},
								readOnly: true,
							}}
							InputLabelProps={{ shrink: true }}
							disabled
						/>

						{/* Email Field - Read-only (populated from API) */}
						<TextField
							label="Email"
							value={formData.email}
							onChange={(e) =>
								setFormData((prev) => ({
									...prev,
									email: e.target.value,
								}))
							}
							size="small"
							sx={GetTextFieldStyle()}
							InputProps={{
								sx: {
									"height": 40,
									"& .MuiInputBase-input": { py: 1 },
									"backgroundColor":
										"action.disabledBackground",
								},
								readOnly: true,
							}}
							InputLabelProps={{ shrink: true }}
							disabled
						/>

						{/* Username Field - hidden but still validated */}
						<input type="hidden" value={formData.username} />

						{/* Sector Selection - Multi-select */}
			<SectorDropdown
				control={control}
				name="sectors"
				watch={watch}
				setValue={setValue}
				trigger={trigger}
				disabled={loading || saving}
				required={true}
			/>
		</UserManagementDialogShell>
	);
}
